import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Loader2, Search, Filter, ChevronDown, ChevronUp, ArrowUpDown,
  DollarSign, Users, TrendingUp, ShieldCheck, FileText, AlertTriangle,
  StickyNote, Send, Eye, Archive, X, MessageSquare, Clock, Building2,
  ArrowRightLeft, Zap, Globe, Lock, LayoutList, LayoutGrid, CheckCircle2,
  AlertCircle, Play, BarChart3
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { fmtUSD, fmtUSDCompact } from "../../utils/currency";
import { formatAddress } from "../../utils/addressFormat";
import { MapThumbnail, MetricPill, StatusBadge, ScoreBadge } from "../shared/DesignSystem";
import AdminServicingTab from "./AdminServicingTab";

function useViewMode(paramName = "view", defaultMode = "table") {
  const location = useLocation();
  const navigate = useNavigate();
  const current = new URLSearchParams(location.search).get(paramName) || defaultMode;
  const setMode = (mode) => {
    const params = new URLSearchParams(location.search);
    if (mode === defaultMode) params.delete(paramName);
    else params.set(paramName, mode);
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "" });
  };
  return [current, setMode];
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOAN_LABELS = {
  fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi",
  construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial",
};

const STAGE_LABELS = {
  submitted: "Submitted", in_review: "In Review", needs_info: "Needs Info",
  underwriting: "Underwriting", approved: "Approved", capital_matching: "Capital Matching",
  matched: "Matched", submitted_to_lender: "Submitted to Lender", terms: "Terms",
  funding: "Funding", funded: "Funded", active: "Active", closed: "Closed",
  declined: "Declined", withdrawn: "Withdrawn",
};

// Source of truth: mirrors ALLOWED_TRANSITIONS from backend models/schemas.py
const ALLOWED_TRANSITIONS = {
  submitted:           ["in_review", "declined", "withdrawn"],
  in_review:           ["needs_info", "underwriting", "declined", "withdrawn"],
  needs_info:          ["in_review", "underwriting", "declined", "withdrawn"],
  underwriting:        ["approved", "declined", "withdrawn"],
  approved:            ["capital_matching", "declined", "withdrawn"],
  capital_matching:    ["matched", "submitted_to_lender", "terms", "declined", "withdrawn"],
  matched:             ["terms", "declined", "withdrawn"],
  submitted_to_lender: ["matched", "terms", "declined", "withdrawn"],
  terms:               ["funding", "declined", "withdrawn"],
  funding:             ["funded", "declined", "withdrawn"],
  funded:              ["active", "declined", "withdrawn"],
  active:              ["closed", "declined", "withdrawn"],
  closed:              ["declined", "withdrawn"],
  declined:            ["withdrawn"],
  withdrawn:           ["declined"],
};

const TERMINAL_STAGES = new Set(["funded", "closed", "declined", "withdrawn"]);

const STAGE_COLORS = {
  submitted: "bg-blue-50 text-blue-600 border-blue-200",
  in_review: "bg-amber-50 text-amber-600 border-amber-200",
  needs_info: "bg-orange-50 text-orange-600 border-orange-200",
  underwriting: "bg-purple-50 text-purple-700 border-purple-200",
  approved: "bg-emerald-50 text-emerald-600 border-emerald-200",
  capital_matching: "bg-indigo-50 text-indigo-600 border-indigo-200",
  matched: "bg-emerald-50 text-emerald-700 border-emerald-200",
  submitted_to_lender: "bg-cyan-50 text-cyan-700 border-cyan-200",
  terms: "bg-violet-50 text-violet-600 border-violet-200",
  funding: "bg-teal-50 text-teal-600 border-teal-200",
  funded: "bg-green-50 text-green-700 border-green-200",
  active: "bg-green-100 text-green-700 border-green-200",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
  declined: "bg-red-50 text-red-600 border-red-200",
  withdrawn: "bg-red-50 text-red-500 border-red-200",
};

const RISK_COLORS = {
  low: "text-emerald-600 bg-emerald-50 border-emerald-200",
  medium: "text-amber-600 bg-amber-50 border-amber-200",
  high: "text-red-600 bg-red-50 border-red-200",
};

const DEPARTMENTS = ["general", "underwriting", "originations", "operations", "investor_relations", "lender_relations", "compliance"];

/* ── Kanban constants ── */
const PIPELINE_STAGES = ["submitted", "in_review", "needs_info", "underwriting", "approved", "capital_matching", "terms", "funding", "funded"];

const NEXT_ACTIONS = {
  submitted: { label: "Start Review", nextStage: "in_review", color: "bg-amber-500 hover:bg-amber-600" },
  in_review: { label: "Begin Underwriting", nextStage: "underwriting", color: "bg-purple-500 hover:bg-purple-600" },
  underwriting: { label: "Approve Deal", nextStage: "approved", color: "bg-emerald-500 hover:bg-emerald-600" },
  approved: { label: "Match Capital", nextStage: "capital_matching", color: "bg-indigo-500 hover:bg-indigo-600" },
  capital_matching: { label: "Move to Terms", nextStage: "terms", color: "bg-violet-500 hover:bg-violet-600" },
  terms: { label: "Start Funding", nextStage: "funding", color: "bg-teal-500 hover:bg-teal-600" },
  funding: { label: "Mark Funded", nextStage: "funded", color: "bg-green-600 hover:bg-green-700" },
  funded: { label: "Complete", nextStage: null, color: "bg-gray-400" },
  needs_info: { label: "Return to Review", nextStage: "in_review", color: "bg-orange-500 hover:bg-orange-600" },
};

const KANBAN_COLORS = {
  submitted: { bg: "bg-blue-50", border: "border-blue-200", header: "bg-blue-100 text-blue-700", dot: "bg-blue-400" },
  in_review: { bg: "bg-amber-50", border: "border-amber-200", header: "bg-amber-100 text-amber-700", dot: "bg-amber-400" },
  needs_info: { bg: "bg-orange-50", border: "border-orange-200", header: "bg-orange-100 text-orange-700", dot: "bg-orange-400" },
  underwriting: { bg: "bg-purple-50", border: "border-purple-200", header: "bg-purple-100 text-purple-700", dot: "bg-purple-400" },
  approved: { bg: "bg-emerald-50", border: "border-emerald-200", header: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-400" },
  capital_matching: { bg: "bg-indigo-50", border: "border-indigo-200", header: "bg-indigo-100 text-indigo-700", dot: "bg-indigo-400" },
  terms: { bg: "bg-violet-50", border: "border-violet-200", header: "bg-violet-100 text-violet-700", dot: "bg-violet-400" },
  funding: { bg: "bg-teal-50", border: "border-teal-200", header: "bg-teal-100 text-teal-700", dot: "bg-teal-400" },
  funded: { bg: "bg-green-50", border: "border-green-200", header: "bg-green-100 text-green-700", dot: "bg-green-500" },
};

export default function DealCommandCenter({ onViewDeal, onEntityNavigate }) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [riskFilter, setRiskFilter] = useState("all");
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [updatingStage, setUpdatingStage] = useState(null);
  const [pathTab, setPathTab] = useState("lending");
  const [convertingDeal, setConvertingDeal] = useState(null);
  const [publishingOpp, setPublishingOpp] = useState(null);
  const [publishModal, setPublishModal] = useState(null);
  const [viewMode, setViewMode] = useViewMode("view", "table");

  // Notes drawer state
  const [notesDrawer, setNotesDrawer] = useState(null);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [noteDept, setNoteDept] = useState("general");
  const [addingNote, setAddingNote] = useState(false);

  // Kanban drag state
  const [draggedDeal, setDraggedDeal] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  const fetchDeals = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/pipeline`);
      setDeals(res.data.deals || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  const fetchNotes = async (leadId) => {
    setNotesLoading(true);
    try {
      const res = await axios.get(`${API}/admin/deals/${leadId}/notes`);
      setNotes(res.data.notes || []);
    } catch (err) { console.error(err); }
    finally { setNotesLoading(false); }
  };

  const openNotes = (deal) => {
    setNotesDrawer(deal);
    fetchNotes(deal.lead_id);
  };

  const addNote = async () => {
    if (!newNote.trim() || !notesDrawer) return;
    setAddingNote(true);
    try {
      await axios.post(`${API}/admin/deals/${notesDrawer.lead_id}/notes`, {
        content: newNote.trim(),
        department: noteDept,
      });
      setNewNote("");
      fetchNotes(notesDrawer.lead_id);
      setDeals(prev => prev.map(d =>
        d.lead_id === notesDrawer.lead_id ? { ...d, notes_count: (d.notes_count || 0) + 1 } : d
      ));
    } catch (err) { console.error(err); }
    finally { setAddingNote(false); }
  };

  const updateStage = async (leadId, newStage) => {
    const deal = deals.find(d => d.lead_id === leadId);
    const currentStageLabel = STAGE_LABELS[deal?.stage] || deal?.stage;
    const newStageLabel = STAGE_LABELS[newStage] || newStage;
    const dealNum = deal?.deal_number || leadId.slice(0, 8);

    // Part 4: Confirmation dialog
    if (!window.confirm(`Move deal ${dealNum} from "${currentStageLabel}" to "${newStageLabel}"?\n\nThis action will be recorded in the audit log.`)) return;

    setUpdatingStage(leadId);
    try {
      await axios.post(`${API}/admin/leads/${leadId}/stage?stage=${newStage}`);
      toast.success(`Deal moved to ${newStageLabel}`);
      setDeals(prev => prev.map(d => d.lead_id === leadId ? { ...d, stage: newStage } : d));
    } catch (err) {
      const detail = err.response?.data?.detail || "Stage update failed";
      // Part 3: Show transition validation error
      if (detail.includes("not permitted")) {
        toast.error(`Cannot move from "${currentStageLabel}" to "${newStageLabel}" — invalid transition.`);
      } else {
        toast.error(detail);
      }
    }
    finally { setUpdatingStage(null); }
  };

  const convertDealPath = async (leadId, newPath) => {
    setConvertingDeal(leadId);
    try {
      await axios.post(`${API}/admin/deals/${leadId}/convert-path`, { deal_path: newPath });
      toast.success(`Deal path changed to ${newPath}`);
      fetchDeals();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to convert deal path");
    } finally { setConvertingDeal(null); }
  };

  const publishOpportunity = async (oppId, terms = {}) => {
    setPublishingOpp(oppId);
    try {
      await axios.post(`${API}/admin/opportunities/${oppId}/publish`, terms);
      toast.success("Opportunity published");
      setPublishModal(null);
      fetchDeals();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to publish");
    } finally { setPublishingOpp(null); }
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  /* ── Kanban drag handlers ── */
  const handleDragStart = (e, deal) => {
    setDraggedDeal(deal);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", deal.lead_id);
  };
  const handleDragOver = (e, stage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverStage !== stage) setDragOverStage(stage);
  };
  const handleDragLeave = () => setDragOverStage(null);
  const handleDrop = (e, targetStage) => {
    e.preventDefault();
    setDragOverStage(null);
    if (draggedDeal && draggedDeal.stage !== targetStage) {
      updateStage(draggedDeal.lead_id, targetStage);
    }
    setDraggedDeal(null);
  };
  const handleDragEnd = () => { setDraggedDeal(null); setDragOverStage(null); };

  // Filter + sort
  const filtered = deals.filter(d => {
    const dp = d.deal_path || "lending";
    if (pathTab === "lending" && dp !== "lending" && dp !== "standard") return false;
    if (pathTab === "opportunities" && dp !== "opportunity" && dp !== "dual") return false;
    if (stageFilter !== "all" && d.stage !== stageFilter) return false;
    if (riskFilter !== "all" && d.risk_tier !== riskFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (d.full_name || "").toLowerCase().includes(q) ||
        (d.property_address || "").toLowerCase().includes(q) ||
        (d.deal_number || "").toLowerCase().includes(q);
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === "asc" ? 1 : -1;
    if (sortField === "created_at") return dir * ((b.created_at || "").localeCompare(a.created_at || ""));
    if (sortField === "amount") return dir * ((a.requested_loan_amount || 0) - (b.requested_loan_amount || 0));
    if (sortField === "risk_score") return dir * ((a.risk_score || 0) - (b.risk_score || 0));
    if (sortField === "capital_raised") return dir * ((a.capital_raised || 0) - (b.capital_raised || 0));
    if (sortField === "interests") return dir * ((a.interests || 0) - (b.interests || 0));
    return 0;
  });

  // KPIs
  const pathDeals = deals.filter(d => {
    const dp = d.deal_path || "lending";
    if (pathTab === "lending") return dp === "lending" || dp === "standard";
    return dp === "opportunity" || dp === "dual";
  });
  const totalVolume = pathDeals.reduce((s, d) => s + (d.requested_loan_amount || d.purchase_price || 0), 0);
  const totalCapital = pathDeals.reduce((s, d) => s + (d.capital_raised || 0), 0);
  const totalInvestors = pathDeals.reduce((s, d) => s + (d.investor_count || 0), 0);
  const totalLenders = pathDeals.reduce((s, d) => s + (d.interests || 0), 0);
  const activeDeals = pathDeals.filter(d => d.stage !== "funded" && d.stage !== "declined").length;
  const fundedDeals = pathDeals.filter(d => d.stage === "funded").length;

  const lendingCount = deals.filter(d => { const dp = d.deal_path || "lending"; return dp === "lending" || dp === "standard"; }).length;
  const oppCount = deals.filter(d => { const dp = d.deal_path || "lending"; return dp === "opportunity" || dp === "dual"; }).length;

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="command-center-loading">
      <Loader2 className="h-8 w-8 animate-spin text-emerald-700" />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="deal-command-center">
      {/* Header: Path Tabs + View Toggle */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        {/* Path Tabs */}
        <div className="flex items-center gap-1 p-1 rounded-xl bg-[#f5f0e8] border border-[#e8e0d0] w-fit" data-testid="deal-path-tabs">
          <button onClick={() => setPathTab("lending")} data-testid="path-tab-lending"
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${pathTab === "lending" ? "bg-white text-emerald-700 shadow-sm border border-emerald-200" : "text-[#888] hover:text-[#555]"}`}>
            Lending Pipeline ({lendingCount})
          </button>
          <button onClick={() => setPathTab("opportunities")} data-testid="path-tab-opportunities"
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${pathTab === "opportunities" ? "bg-white text-indigo-700 shadow-sm border border-indigo-200" : "text-[#888] hover:text-[#555]"}`}>
            Investment Opportunities ({oppCount})
          </button>
          <button onClick={() => setPathTab("servicing")} data-testid="path-tab-servicing"
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${pathTab === "servicing" ? "bg-white text-[#8B7340] shadow-sm border border-[#E5C98B]" : "text-[#888] hover:text-[#555]"}`}>
            Servicing ({fundedDeals})
          </button>
        </div>

        {/* View Toggle — Monday.com style */}
        {pathTab !== "servicing" && (
        <div className="flex items-center gap-0.5 p-1 rounded-lg bg-[#f5f0e8] border border-[#e8e0d0]" data-testid="view-mode-toggle">
          <button onClick={() => setViewMode("table")} data-testid="view-mode-table"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === "table" ? "bg-white text-emerald-700 shadow-sm" : "text-[#888] hover:text-[#555]"}`}>
            <LayoutList className="h-3.5 w-3.5" /> Table
          </button>
          <button onClick={() => setViewMode("board")} data-testid="view-mode-board"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === "board" ? "bg-white text-emerald-700 shadow-sm" : "text-[#888] hover:text-[#555]"}`}>
            <LayoutGrid className="h-3.5 w-3.5" /> Board
          </button>
          <button onClick={() => setViewMode("cards")} data-testid="view-mode-cards"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === "cards" ? "bg-white text-emerald-700 shadow-sm" : "text-[#888] hover:text-[#555]"}`}>
            <Building2 className="h-3.5 w-3.5" /> Cards
          </button>
          <button onClick={() => setViewMode("analytics")} data-testid="view-mode-analytics"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === "analytics" ? "bg-white text-emerald-700 shadow-sm" : "text-[#888] hover:text-[#555]"}`}>
            <BarChart3 className="h-3.5 w-3.5" /> Analytics
          </button>
        </div>
        )}
      </div>

      {/* KPI Strip */}
      {pathTab !== "servicing" && (
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: "Active Deals", value: activeDeals, icon: TrendingUp, color: "text-emerald-600" },
          { label: "Pipeline Volume", value: `$${(totalVolume / 1e6).toFixed(1)}M`, icon: DollarSign, color: "text-[#b89d5a]" },
          { label: "Capital Raised", value: `$${(totalCapital / 1e6).toFixed(2)}M`, icon: DollarSign, color: "text-blue-600" },
          { label: "Lenders Active", value: totalLenders, icon: Building2, color: "text-cyan-600" },
          { label: "Investors In", value: totalInvestors, icon: Users, color: "text-amber-600" },
          { label: "Funded", value: fundedDeals, icon: ShieldCheck, color: "text-green-600" },
        ].map((kpi, i) => (
          <motion.div key={kpi.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            className="rounded-xl border border-[#e8e0d0] bg-white p-3" data-testid={`cc-kpi-${kpi.label.toLowerCase().replace(/ /g, '-')}`}>
            <div className="flex items-center gap-2 mb-1">
              <kpi.icon className={`h-3.5 w-3.5 ${kpi.color}`} />
              <span className="text-[9px] uppercase tracking-wider text-[#999] font-semibold">{kpi.label}</span>
            </div>
            <div className={`text-xl font-bold ${kpi.color}`}>{kpi.value}</div>
          </motion.div>
        ))}
      </div>
      )}

      {/* Servicing Tab Content */}
      {pathTab === "servicing" && <AdminServicingTab />}

      {/* Filters */}
      {pathTab !== "servicing" && (
      <div className="flex flex-wrap gap-3 items-center" data-testid="cc-filters">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#aaa]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search deals..."
            data-testid="cc-search"
            className="w-full pl-10 pr-3 py-2 rounded-xl border border-[#e8e0d0] bg-white text-sm focus:outline-none focus:border-emerald-400 transition-colors" />
        </div>
        <select value={stageFilter} onChange={e => setStageFilter(e.target.value)} data-testid="cc-stage-filter"
          className="px-3 py-2 rounded-xl border border-[#e8e0d0] bg-white text-xs focus:outline-none">
          <option value="all">All Stages</option>
          {Object.entries(STAGE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={riskFilter} onChange={e => setRiskFilter(e.target.value)} data-testid="cc-risk-filter"
          className="px-3 py-2 rounded-xl border border-[#e8e0d0] bg-white text-xs focus:outline-none">
          <option value="all">All Risk</option>
          <option value="low">Low Risk</option>
          <option value="medium">Medium Risk</option>
          <option value="high">High Risk</option>
        </select>
        <span className="text-xs text-[#999]">{sorted.length} of {deals.length} deals</span>
      </div>
      )}

      {/* ════════ TABLE VIEW ════════ */}
      {pathTab !== "servicing" && viewMode === "table" && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white overflow-hidden" data-testid="cc-deals-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#faf7f2] border-b border-[#e8e0d0]">
                  <SortHeader label="Deal" field="created_at" current={sortField} dir={sortDir} onSort={toggleSort} />
                  <th className="px-3 py-3 text-left text-[10px] uppercase tracking-wider text-[#888] font-semibold">Type</th>
                  <SortHeader label="Amount" field="amount" current={sortField} dir={sortDir} onSort={toggleSort} align="right" />
                  <SortHeader label="Score" field="risk_score" current={sortField} dir={sortDir} onSort={toggleSort} align="center" />
                  <th className="px-3 py-3 text-center text-[10px] uppercase tracking-wider text-[#888] font-semibold">Stage</th>
                  <SortHeader label="Capital Raised" field="capital_raised" current={sortField} dir={sortDir} onSort={toggleSort} align="right" />
                  <SortHeader label="Lenders" field="interests" current={sortField} dir={sortDir} onSort={toggleSort} align="center" />
                  <th className="px-3 py-3 text-center text-[10px] uppercase tracking-wider text-[#888] font-semibold">Investors</th>
                  <th className="px-3 py-3 text-center text-[10px] uppercase tracking-wider text-[#888] font-semibold">Offers</th>
                  <th className="px-3 py-3 text-right text-[10px] uppercase tracking-wider text-[#888] font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#f0ebe0]">
                {sorted.map(deal => (
                  <DealRow key={deal.lead_id} deal={deal} pathTab={pathTab}
                    onView={() => onEntityNavigate?.("deal", deal.lead_id)}
                    onNotes={() => openNotes(deal)}
                    onStageChange={(stage) => updateStage(deal.lead_id, stage)}
                    onConvertPath={(newPath) => convertDealPath(deal.lead_id, newPath)}
                    onPublish={() => deal.opportunity_id && deal.opp_status === "coming_soon" && setPublishModal({ deal, opportunity_id: deal.opportunity_id })}
                    updatingStage={updatingStage} convertingDeal={convertingDeal} publishingOpp={publishingOpp} />
                ))}
                {sorted.length === 0 && (
                  <tr><td colSpan={10} className="text-center py-16 text-[#999]">No deals match your filters</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ════════ BOARD (KANBAN) VIEW ════════ */}
      {pathTab !== "servicing" && viewMode === "board" && (
        <div className="overflow-x-auto pb-4" data-testid="cc-kanban-board">
          <div className="flex gap-4 min-w-max">
            {PIPELINE_STAGES.map(stage => {
              const stageDeals = sorted.filter(d => d.stage === stage);
              const colors = KANBAN_COLORS[stage] || KANBAN_COLORS.submitted;
              const isDragTarget = dragOverStage === stage && draggedDeal?.stage !== stage;
              return (
                <div key={stage}
                  className={`flex-shrink-0 w-[280px] rounded-2xl border ${isDragTarget ? "border-emerald-400 ring-2 ring-emerald-200" : colors.border} ${colors.bg} overflow-hidden transition-all duration-200`}
                  data-testid={`pipeline-col-${stage}`}
                  onDragOver={(e) => handleDragOver(e, stage)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, stage)}>
                  <div className={`px-4 py-3 ${colors.header} flex items-center justify-between`}>
                    <div className="flex items-center gap-2">
                      <div className={`h-2.5 w-2.5 rounded-full ${colors.dot}`} />
                      <span className="text-xs font-bold uppercase tracking-wider">{STAGE_LABELS[stage] || stage}</span>
                    </div>
                    <span className="text-xs font-bold bg-white/60 px-2 py-0.5 rounded-full">{stageDeals.length}</span>
                  </div>
                  <div className="p-3 space-y-3 max-h-[60vh] overflow-y-auto">
                    {stageDeals.length === 0 && (
                      <div className={`text-center py-6 text-xs ${isDragTarget ? "text-emerald-500 font-medium" : "text-[#aaa]"}`}>
                        {isDragTarget ? "Drop here" : "No deals"}
                      </div>
                    )}
                    {stageDeals.map(deal => {
                      const nextAction = NEXT_ACTIONS[stage];
                      return (
                        <motion.div key={deal.lead_id} layout initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                          draggable onDragStart={(e) => handleDragStart(e, deal)} onDragEnd={handleDragEnd}
                          className={`rounded-xl bg-white border border-[#e8e0d0] p-3.5 shadow-sm hover:shadow-md transition-all cursor-grab active:cursor-grabbing group ${draggedDeal?.lead_id === deal.lead_id ? "opacity-40 scale-95" : ""}`}
                          onClick={() => onEntityNavigate?.("deal", deal.lead_id)}
                          data-testid={`pipeline-card-${deal.lead_id}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="text-sm font-semibold text-[#0A0A0A] truncate max-w-[180px] hover:text-emerald-700 transition-colors"
                                data-testid={`pipeline-name-${deal.lead_id}`}>{deal.full_name}</div>
                              {deal.deal_number && <div className="text-[10px] font-mono text-emerald-600 font-semibold">{deal.deal_number}</div>}
                            </div>
                            <span className="px-1.5 py-0.5 rounded-md bg-[#E5C98B]/15 text-[#b89d5a] text-[10px] font-medium shrink-0">
                              {LOAN_LABELS[deal.loan_type] || deal.loan_type}
                            </span>
                          </div>
                          <div className="text-xs text-[#888] truncate mb-2">{deal.property_address || "—"}</div>
                          <div className="flex items-center gap-3 text-xs">
                            <span className="font-semibold text-[#0A0A0A]">{fmtUSD(deal.requested_loan_amount || deal.purchase_price || 0)}</span>
                            {deal.risk_score > 0 && (
                              <span className={`font-bold ${deal.risk_score >= 75 ? "text-emerald-600" : deal.risk_score >= 60 ? "text-amber-600" : "text-orange-600"}`}>
                                Score: {deal.risk_score}
                              </span>
                            )}
                            {deal.lender_views > 0 && (
                              <span className="flex items-center gap-0.5 text-blue-500"><Eye className="h-2.5 w-2.5" />{deal.lender_views}</span>
                            )}
                          </div>
                          {/* Completeness bar */}
                          {deal.completeness && (
                            <div className="mt-2 flex items-center gap-1.5">
                              <div className="flex-1 h-1.5 rounded-full bg-[#e8e0d0] overflow-hidden">
                                <div className={`h-full rounded-full ${deal.completeness.pct >= 80 ? "bg-emerald-400" : deal.completeness.pct >= 50 ? "bg-amber-400" : "bg-orange-400"}`}
                                  style={{ width: `${deal.completeness.pct}%` }} />
                              </div>
                              <span className="text-[10px] text-[#aaa]">{deal.completeness.pct}%</span>
                              {deal.completeness.ready_for_review ? <CheckCircle2 className="h-3 w-3 text-emerald-500" /> : <AlertCircle className="h-3 w-3 text-amber-500" />}
                            </div>
                          )}
                          {/* Action + drag hint */}
                          <div className="mt-2 pt-2 border-t border-[#e8e0d0] flex items-center gap-1.5">
                            {nextAction && nextAction.nextStage && (
                              <button onClick={(e) => { e.stopPropagation(); updateStage(deal.lead_id, nextAction.nextStage); }}
                                disabled={updatingStage === deal.lead_id}
                                data-testid={`stage-action-${deal.lead_id}`}
                                className={`flex-1 px-2 py-1.5 rounded-lg text-[10px] font-bold text-white ${nextAction.color} transition-all shadow-sm`}>
                                {updatingStage === deal.lead_id ? <Loader2 className="h-3 w-3 animate-spin mx-auto" /> : nextAction.label}
                              </button>
                            )}
                            <span className="text-[8px] text-[#bbb] italic shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">or drag</span>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ════════ CARDS VIEW (Design System) ════════ */}
      {pathTab !== "servicing" && viewMode === "cards" && (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3" data-testid="cc-cards-view">
          {sorted.map((deal, i) => (
            <AdminDealCard
              key={deal.lead_id}
              deal={deal}
              index={i}
              onView={() => onEntityNavigate?.("deal", deal.lead_id)}
              onNotes={() => openNotes(deal)}
              onStageChange={(stage) => updateStage(deal.lead_id, stage)}
              updatingStage={updatingStage}
            />
          ))}
          {sorted.length === 0 && (
            <div className="md:col-span-3 text-center py-16 text-sm" style={{ color: "var(--ic-text-secondary)" }}>No deals match your filters</div>
          )}
        </div>
      )}

      {/* ════════ ANALYTICS VIEW ════════ */}
      {pathTab !== "servicing" && viewMode === "analytics" && (
        <DealAnalyticsView deals={deals} />
      )}

      {/* Notes Drawer */}
      <AnimatePresence>
        {notesDrawer && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex justify-end" onClick={() => setNotesDrawer(null)}>
            <motion.div initial={{ x: 400 }} animate={{ x: 0 }} exit={{ x: 400 }} transition={{ type: "spring", damping: 25 }}
              onClick={e => e.stopPropagation()}
              className="w-full max-w-md bg-white h-full shadow-2xl flex flex-col" data-testid="notes-drawer">
              <div className="px-5 py-4 border-b border-[#e8e0d0] flex items-center justify-between shrink-0">
                <div>
                  <h3 className="text-sm font-bold text-[#0A0A0A]">Internal Notes</h3>
                  <p className="text-xs text-[#888]">{notesDrawer.deal_number || notesDrawer.full_name}</p>
                </div>
                <button onClick={() => setNotesDrawer(null)} className="p-1.5 rounded-lg hover:bg-[#f5f0e8] transition-colors" data-testid="close-notes-drawer">
                  <X className="h-4 w-4 text-[#888]" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {notesLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>
                ) : notes.length === 0 ? (
                  <div className="text-center py-12 text-[#999] text-sm">No notes yet. Add the first one below.</div>
                ) : notes.map(note => (
                  <div key={note.note_id} className="rounded-xl border border-[#e8e0d0] bg-[#faf7f2] p-3.5" data-testid={`note-${note.note_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-[#0A0A0A]">{note.author_name}</span>
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-[#e8e0d0] text-[#666]">{note.department?.replace(/_/g, " ")}</span>
                      </div>
                      <span className="text-[10px] text-[#aaa]">{note.created_at ? new Date(note.created_at).toLocaleString() : ""}</span>
                    </div>
                    <p className="text-sm text-[#333] whitespace-pre-wrap">{note.content}</p>
                  </div>
                ))}
              </div>
              <div className="shrink-0 border-t border-[#e8e0d0] p-4 space-y-2" data-testid="add-note-form">
                <div className="flex gap-2">
                  <select value={noteDept} onChange={e => setNoteDept(e.target.value)}
                    className="px-2 py-1.5 rounded-lg border border-[#e8e0d0] text-xs bg-white focus:outline-none" data-testid="note-dept-select">
                    {DEPARTMENTS.map(d => <option key={d} value={d}>{d.replace(/_/g, " ")}</option>)}
                  </select>
                </div>
                <div className="flex gap-2">
                  <textarea value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add internal note..."
                    rows={2} className="flex-1 rounded-xl border border-[#e8e0d0] px-3 py-2 text-sm resize-none focus:outline-none focus:border-emerald-400" data-testid="note-input" />
                  <button onClick={addNote} disabled={addingNote || !newNote.trim()}
                    className="px-4 rounded-xl bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-40 self-end" data-testid="note-submit-btn">
                    {addingNote ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Add"}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Publish Modal */}
      <AnimatePresence>
        {publishModal && (
          <PublishModal deal={publishModal.deal} opportunityId={publishModal.opportunity_id}
            onPublish={(terms) => publishOpportunity(publishModal.opportunity_id, terms)}
            onClose={() => setPublishModal(null)} publishing={!!publishingOpp} />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Sub-components ── */

function PublishModal({ deal, opportunityId, onPublish, onClose, publishing }) {
  const [oppType, setOppType] = useState("");
  const [yield_, setYield] = useState("");
  const [termMonths, setTermMonths] = useState("");
  const [minInvestment, setMinInvestment] = useState("25000");
  const [targetRaise, setTargetRaise] = useState(deal?.requested_loan_amount?.toString() || "");
  const [notes, setNotes] = useState("");
  const snap = deal || {};
  const handleSubmit = () => {
    const terms = {};
    if (oppType.trim()) terms.opportunity_type = oppType.trim();
    if (yield_) terms.projected_yield = parseFloat(yield_);
    if (termMonths) terms.projected_term_months = parseInt(termMonths);
    if (minInvestment) terms.min_investment = parseInt(minInvestment);
    if (targetRaise) terms.target_raise = parseInt(targetRaise);
    if (notes.trim()) terms.notes = notes.trim();
    onPublish(terms);
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="publish-modal">
        <div className="px-4 sm:px-6 py-4 border-b border-[#e8e0d0]">
          <h3 className="text-base font-bold text-[#0A0A0A]">Promote to Open</h3>
          <p className="text-xs text-[#888] mt-0.5">Set investment terms before publishing {snap.deal_number || "this opportunity"} to investors.</p>
        </div>
        <div className="px-4 sm:px-6 py-5 space-y-4">
          <div className="p-3 rounded-xl bg-[#faf7f2] border border-[#e8e0d0]">
            <div className="flex items-center justify-between text-xs"><span className="text-[#888]">Deal</span><span className="font-semibold text-[#0A0A0A]">{snap.deal_number || snap.full_name}</span></div>
            <div className="flex items-center justify-between text-xs mt-1"><span className="text-[#888]">Type</span><span className="font-semibold">{LOAN_LABELS[snap.loan_type] || snap.loan_type}</span></div>
            {snap.property_address && <div className="flex items-center justify-between text-xs mt-1"><span className="text-[#888]">Property</span><span className="font-semibold text-[#555] truncate max-w-[200px]">{snap.property_address}</span></div>}
          </div>
          <div><label className="text-xs font-semibold text-[#444] block mb-1">Opportunity Type</label>
            <input type="text" value={oppType} onChange={e => setOppType(e.target.value)} placeholder="e.g. Equity Co-Invest, Mezzanine Debt..." data-testid="publish-opp-type-input"
              className="w-full px-3 py-2 rounded-xl border border-[#e8e0d0] text-sm focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-[#444] block mb-1">Projected Yield (%)</label>
              <input type="number" step="0.1" value={yield_} onChange={e => setYield(e.target.value)} placeholder="e.g. 12.5" data-testid="publish-yield-input"
                className="w-full px-3 py-2 rounded-xl border border-[#e8e0d0] text-sm focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none" /></div>
            <div><label className="text-xs font-semibold text-[#444] block mb-1">Term (months)</label>
              <input type="number" value={termMonths} onChange={e => setTermMonths(e.target.value)} placeholder="e.g. 12" data-testid="publish-term-input"
                className="w-full px-3 py-2 rounded-xl border border-[#e8e0d0] text-sm focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-[#444] block mb-1">Target Raise ($)</label>
              <input type="number" value={targetRaise} onChange={e => setTargetRaise(e.target.value)} placeholder="e.g. 200000" data-testid="publish-target-input"
                className="w-full px-3 py-2 rounded-xl border border-[#e8e0d0] text-sm focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none" /></div>
            <div><label className="text-xs font-semibold text-[#444] block mb-1">Min Investment ($)</label>
              <input type="number" value={minInvestment} onChange={e => setMinInvestment(e.target.value)} placeholder="e.g. 25000" data-testid="publish-min-input"
                className="w-full px-3 py-2 rounded-xl border border-[#e8e0d0] text-sm focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none" /></div>
          </div>
          <div><label className="text-xs font-semibold text-[#444] block mb-1">Notes for Investors (optional)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Key highlights, risk factors..." rows={2} data-testid="publish-notes-input"
              className="w-full px-3 py-2 rounded-xl border border-[#e8e0d0] text-sm focus:border-emerald-400 outline-none resize-none" /></div>
        </div>
        <div className="px-4 sm:px-6 py-4 border-t border-[#e8e0d0] flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-[#e8e0d0] text-sm text-[#666] hover:bg-[#faf7f2] transition-colors" data-testid="publish-cancel-btn">Cancel</button>
          <button onClick={handleSubmit} disabled={publishing} data-testid="publish-confirm-btn"
            className="px-5 py-2 rounded-xl bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-600 disabled:opacity-50 transition-colors flex items-center gap-2">
            {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />} Publish to Investors
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function SortHeader({ label, field, current, dir, onSort, align = "left" }) {
  const active = current === field;
  const alignClass = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  return (
    <th className={`px-3 py-3 ${alignClass} text-[10px] uppercase tracking-wider font-semibold cursor-pointer select-none group`} onClick={() => onSort(field)}>
      <span className={`inline-flex items-center gap-1 ${active ? "text-emerald-700" : "text-[#888]"}`}>
        {label} <ArrowUpDown className={`h-3 w-3 ${active ? "text-emerald-600" : "text-[#ccc] group-hover:text-[#999]"} transition-colors`} />
      </span>
    </th>
  );
}

function DealRow({ deal, pathTab, onView, onNotes, onStageChange, onConvertPath, onPublish, updatingStage, convertingDeal, publishingOpp }) {
  const [showStages, setShowStages] = useState(false);
  const stageColor = STAGE_COLORS[deal.stage] || "bg-slate-100 text-slate-600 border-slate-200";
  const riskColor = RISK_COLORS[deal.risk_tier] || "";
  const isConverting = convertingDeal === deal.lead_id;
  const isPublishing = publishingOpp === deal.opportunity_id;

  return (
    <tr className="hover:bg-[#faf7f2]/80 transition-colors group" data-testid={`cc-deal-${deal.lead_id}`}>
      <td className="px-3 py-3 min-w-[200px]">
        <button onClick={onView} className="text-left group/name" data-testid={`cc-view-${deal.lead_id}`}>
          <div className="text-sm font-semibold text-[#0A0A0A] group-hover/name:text-emerald-700 underline decoration-emerald-300/50 underline-offset-2 transition-colors">{deal.full_name}</div>
          {deal.deal_number && <div className="text-[10px] font-mono font-bold text-emerald-600">{deal.deal_number}</div>}
          <div className="text-[10px] text-[#999] truncate max-w-[180px]">{deal.property_address || "—"}</div>
        </button>
        <div className="flex items-center gap-1 mt-1">
          {deal.deal_path === "dual" && <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-purple-50 text-purple-600 border border-purple-200">DUAL</span>}
          {deal.opp_status && (
            <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold border ${deal.opp_status === "coming_soon" ? "bg-indigo-50 text-indigo-600 border-indigo-200" : deal.opp_status === "open" ? "bg-emerald-50 text-emerald-600 border-emerald-200" : "bg-gray-100 text-gray-500 border-gray-200"}`} data-testid={`cc-opp-status-${deal.lead_id}`}>
              {deal.opp_status === "coming_soon" ? "COMING SOON" : deal.opp_status?.toUpperCase()}
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-3"><span className="px-2 py-0.5 rounded text-[10px] font-medium bg-[#f5f0e8] border border-[#e8e0d0] text-[#555]">{LOAN_LABELS[deal.loan_type] || deal.loan_type}</span></td>
      <td className="px-3 py-3 text-right font-semibold text-[#0A0A0A]">${((deal.requested_loan_amount || deal.purchase_price || 0) / 1e3).toFixed(0)}k</td>
      <td className="px-3 py-3 text-center">
        {deal.risk_score ? (
          <div className="flex flex-col items-center gap-0.5">
            <span className={`text-sm font-bold ${deal.risk_score >= 75 ? "text-emerald-600" : deal.risk_score >= 60 ? "text-amber-600" : "text-red-600"}`}>{deal.risk_score}</span>
            {deal.risk_tier && <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase border ${riskColor}`}>{deal.risk_tier}</span>}
          </div>
        ) : <span className="text-[#ccc]">—</span>}
      </td>
      <td className="px-3 py-3 text-center relative">
        {TERMINAL_STAGES.has(deal.stage) ? (
          /* Terminal stage — show static badge, no dropdown */
          <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border ${stageColor}`} data-testid={`cc-stage-${deal.lead_id}`}>
            {STAGE_LABELS[deal.stage] || deal.stage}
          </span>
        ) : (
          /* Active stage — contextual "Move to..." dropdown */
          <>
            <button onClick={() => setShowStages(!showStages)} className={`px-2 py-1 rounded-lg text-[10px] font-bold border ${stageColor} cursor-pointer hover:opacity-80 transition-opacity`} data-testid={`cc-stage-${deal.lead_id}`}>
              {STAGE_LABELS[deal.stage] || deal.stage} <ChevronDown className="inline h-3 w-3 ml-0.5" />
            </button>
            {showStages && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowStages(false)} />
                <div className="absolute z-20 top-full mt-1 left-1/2 -translate-x-1/2 bg-white rounded-xl border border-[#e8e0d0] shadow-lg py-1 min-w-[160px]" data-testid={`cc-stage-dropdown-${deal.lead_id}`}>
                  <div className="px-3 py-1 text-[8px] uppercase tracking-wider text-[#999] font-bold border-b border-[#eee] mb-0.5">Move to...</div>
                  {(ALLOWED_TRANSITIONS[deal.stage] || []).map(s => (
                    <button key={s} onClick={() => { onStageChange(s); setShowStages(false); }}
                      disabled={updatingStage === deal.lead_id}
                      className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[#faf7f2] transition-colors disabled:opacity-40 text-[#444] ${
                        ["declined", "withdrawn"].includes(s) ? "text-red-500 hover:bg-red-50" : ""
                      }`}
                      data-testid={`cc-stage-option-${s}-${deal.lead_id}`}>
                      {STAGE_LABELS[s] || s}
                    </button>
                  ))}
                  {(!ALLOWED_TRANSITIONS[deal.stage] || ALLOWED_TRANSITIONS[deal.stage].length === 0) && (
                    <div className="px-3 py-1.5 text-[10px] text-[#999] italic">No transitions available</div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </td>
      <td className="px-3 py-3 text-right">{deal.capital_raised > 0 ? <span className="text-sm font-semibold text-blue-600">${(deal.capital_raised / 1e3).toFixed(0)}k</span> : <span className="text-[#ccc]">—</span>}</td>
      <td className="px-3 py-3 text-center">
        <span className="text-sm font-semibold text-[#333]">{deal.interests || 0}</span>
        {deal.matched_lenders > 0 && <div className="text-[9px] text-[#999]">{deal.matched_lenders} matched</div>}
      </td>
      <td className="px-3 py-3 text-center"><span className="text-sm font-semibold text-[#333]">{deal.investor_count || 0}</span></td>
      <td className="px-3 py-3 text-center">{deal.term_sheets > 0 ? <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-violet-50 text-violet-700 border border-violet-200">{deal.term_sheets} TS</span> : <span className="text-[#ccc]">—</span>}</td>
      <td className="px-3 py-3 text-right">
        <div className="flex items-center justify-end gap-1.5 opacity-60 group-hover:opacity-100 transition-opacity">
          <button onClick={onView} title="View Details" className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-700 transition-colors" data-testid={`cc-action-view-${deal.lead_id}`}><Eye className="h-3.5 w-3.5" /></button>
          <button onClick={onNotes} title="Internal Notes" className="relative p-1.5 rounded-lg hover:bg-amber-50 text-amber-600 transition-colors" data-testid={`cc-action-notes-${deal.lead_id}`}>
            <StickyNote className="h-3.5 w-3.5" />
            {deal.notes_count > 0 && <span className="absolute -top-1 -right-1 px-1 py-0.5 rounded-full text-[7px] font-bold bg-amber-500 text-white leading-none">{deal.notes_count}</span>}
          </button>
          {pathTab === "lending" && (
            <button onClick={() => onConvertPath("opportunity")} disabled={isConverting} title="Convert to Investment Opportunity"
              className="p-1.5 rounded-lg hover:bg-indigo-50 text-indigo-600 transition-colors disabled:opacity-40" data-testid={`cc-convert-to-opp-${deal.lead_id}`}>
              {isConverting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRightLeft className="h-3.5 w-3.5" />}
            </button>
          )}
          {pathTab === "opportunities" && (
            <>
              <button onClick={() => onConvertPath("lending")} disabled={isConverting} title="Revert to Lending Only"
                className="p-1.5 rounded-lg hover:bg-orange-50 text-orange-600 transition-colors disabled:opacity-40" data-testid={`cc-revert-to-lending-${deal.lead_id}`}>
                {isConverting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRightLeft className="h-3.5 w-3.5" />}
              </button>
              {deal.opp_status === "coming_soon" && deal.opportunity_id && (
                <button onClick={onPublish} disabled={isPublishing} title="Publish to Investors"
                  className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 transition-colors disabled:opacity-40" data-testid={`cc-publish-opp-${deal.lead_id}`}>
                  {isPublishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Globe className="h-3.5 w-3.5" />}
                </button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
}


function DealAnalyticsView({ deals }) {
  const STAGE_ORDER = ["new_lead", "qualifying", "processing", "underwriting", "approved", "funded", "closed", "withdrawn"];

  // Stage distribution
  const stageCounts = {};
  STAGE_ORDER.forEach(s => { stageCounts[s] = 0; });
  deals.forEach(d => { const s = d.stage || "new_lead"; stageCounts[s] = (stageCounts[s] || 0) + 1; });
  const maxStage = Math.max(...Object.values(stageCounts), 1);

  // Risk distribution
  const riskCounts = { low: 0, medium: 0, high: 0 };
  deals.forEach(d => { const t = d.risk_tier || "medium"; riskCounts[t] = (riskCounts[t] || 0) + 1; });

  // Conversion funnel: each stage as % of total
  const totalDeals = deals.length || 1;

  // Avg time proxy: deals without movement for 48h+
  const now = new Date();
  const staleDays = 2;
  const staleDeals = deals.filter(d => {
    if (!d.updated_at) return false;
    const diff = (now - new Date(d.updated_at)) / (1000 * 60 * 60 * 24);
    return diff > staleDays && d.stage !== "funded" && d.stage !== "closed" && d.stage !== "withdrawn";
  });

  // Bottleneck: stage with highest stale count
  const staleByStage = {};
  staleDeals.forEach(d => { staleByStage[d.stage] = (staleByStage[d.stage] || 0) + 1; });
  const bottleneck = Object.entries(staleByStage).sort((a, b) => b[1] - a[1])[0];

  // Loan type breakdown
  const loanTypeCounts = {};
  deals.forEach(d => { const t = d.loan_type || "other"; loanTypeCounts[t] = (loanTypeCounts[t] || 0) + 1; });

  const STAGE_COLORS = {
    new_lead: "bg-slate-400", qualifying: "bg-amber-400", processing: "bg-blue-400",
    underwriting: "bg-cyan-400", approved: "bg-emerald-400", funded: "bg-green-500",
    closed: "bg-gray-400", withdrawn: "bg-red-300",
  };

  const RISK_COLORS = { low: "bg-emerald-400", medium: "bg-amber-400", high: "bg-red-400" };

  return (
    <div className="space-y-5" data-testid="deal-analytics-view">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
          <div className="text-[9px] uppercase tracking-wider text-[#999] font-semibold mb-1">Total Deals</div>
          <div className="text-2xl font-bold text-[#0A0A0A]">{deals.length}</div>
        </div>
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
          <div className="text-[9px] uppercase tracking-wider text-[#999] font-semibold mb-1">Stalled (48h+)</div>
          <div className={`text-2xl font-bold ${staleDeals.length > 0 ? "text-red-600" : "text-emerald-600"}`}>{staleDeals.length}</div>
        </div>
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
          <div className="text-[9px] uppercase tracking-wider text-[#999] font-semibold mb-1">Bottleneck Stage</div>
          <div className="text-lg font-bold text-amber-600">{bottleneck ? (STAGE_LABELS[bottleneck[0]] || bottleneck[0]) : "None"}</div>
          {bottleneck && <div className="text-[10px] text-[#999]">{bottleneck[1]} deals stuck</div>}
        </div>
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
          <div className="text-[9px] uppercase tracking-wider text-[#999] font-semibold mb-1">Funding Rate</div>
          <div className="text-2xl font-bold text-emerald-600">
            {deals.length > 0 ? `${((stageCounts.funded || 0) / deals.length * 100).toFixed(0)}%` : "0%"}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Stage Distribution */}
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-5" data-testid="analytics-stage-dist">
          <h4 className="text-xs font-bold text-[#444] uppercase tracking-wider mb-4 flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-[#b89d5a]" /> Pipeline Stage Distribution
          </h4>
          <div className="space-y-2.5">
            {STAGE_ORDER.map(stage => {
              const count = stageCounts[stage] || 0;
              const pct = (count / maxStage) * 100;
              return (
                <div key={stage} className="flex items-center gap-3">
                  <span className="text-[10px] font-semibold text-[#555] w-24 truncate">{STAGE_LABELS[stage] || stage}</span>
                  <div className="flex-1 h-6 rounded-full bg-[#faf7f2] border border-[#e8e0d0] overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${Math.max(pct, 2)}%` }}
                      transition={{ duration: 0.5, delay: 0.05 }}
                      className={`h-full rounded-full ${STAGE_COLORS[stage] || "bg-gray-300"} flex items-center justify-end pr-2`}
                      style={{ opacity: 0.85 }}>
                      {pct > 20 && <span className="text-[9px] text-white font-bold">{count}</span>}
                    </motion.div>
                  </div>
                  {pct <= 20 && <span className="text-xs font-bold text-[#555] w-6 text-right">{count}</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Conversion Funnel */}
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-5" data-testid="analytics-funnel">
          <h4 className="text-xs font-bold text-[#444] uppercase tracking-wider mb-4 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-600" /> Conversion Funnel
          </h4>
          <div className="space-y-1.5">
            {STAGE_ORDER.filter(s => s !== "withdrawn" && s !== "closed").map((stage, i) => {
              const cumulative = STAGE_ORDER.slice(i).reduce((sum, s) => sum + (stageCounts[s] || 0), 0);
              const pct = (cumulative / totalDeals) * 100;
              return (
                <div key={stage} className="flex items-center gap-3">
                  <span className="text-[10px] font-semibold text-[#555] w-24 truncate">{STAGE_LABELS[stage] || stage}</span>
                  <div className="flex-1 flex items-center gap-2">
                    <div className="flex-1 h-8 rounded-md bg-[#faf7f2] border border-[#e8e0d0] overflow-hidden flex items-center" style={{ maxWidth: `${Math.max(pct, 10)}%` }}>
                      <motion.div initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ duration: 0.5, delay: i * 0.08 }}
                        className={`h-full ${STAGE_COLORS[stage] || "bg-gray-300"} rounded-md`} style={{ opacity: 0.7 }} />
                    </div>
                    <span className="text-[10px] font-bold text-[#555]">{cumulative} ({pct.toFixed(0)}%)</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Risk Distribution */}
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-5" data-testid="analytics-risk">
          <h4 className="text-xs font-bold text-[#444] uppercase tracking-wider mb-4 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-blue-500" /> Risk Distribution
          </h4>
          <div className="flex items-end gap-6 justify-center py-4">
            {Object.entries(riskCounts).map(([tier, count]) => {
              const pct = totalDeals > 0 ? (count / totalDeals) * 100 : 0;
              return (
                <div key={tier} className="flex flex-col items-center gap-2">
                  <span className="text-xl font-bold text-[#0A0A0A]">{count}</span>
                  <div className={`w-16 rounded-t-lg ${RISK_COLORS[tier]}`} style={{ height: `${Math.max(pct * 1.5, 8)}px`, opacity: 0.8 }} />
                  <span className="text-[10px] uppercase tracking-wider text-[#888] font-semibold">{tier}</span>
                  <span className="text-[10px] text-[#aaa]">{pct.toFixed(0)}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Loan Type Breakdown */}
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-5" data-testid="analytics-loan-types">
          <h4 className="text-xs font-bold text-[#444] uppercase tracking-wider mb-4 flex items-center gap-2">
            <FileText className="h-4 w-4 text-purple-500" /> Loan Type Breakdown
          </h4>
          <div className="space-y-2">
            {Object.entries(loanTypeCounts).sort((a, b) => b[1] - a[1]).map(([type, count]) => {
              const pct = (count / totalDeals) * 100;
              const COLORS = ["bg-emerald-400", "bg-blue-400", "bg-amber-400", "bg-purple-400", "bg-cyan-400", "bg-red-300"];
              const idx = Object.keys(loanTypeCounts).indexOf(type);
              return (
                <div key={type} className="flex items-center gap-3">
                  <span className="text-[10px] font-semibold text-[#555] w-28 truncate capitalize">{type.replace(/_/g, " ")}</span>
                  <div className="flex-1 h-5 rounded-full bg-[#faf7f2] border border-[#e8e0d0] overflow-hidden">
                    <div className={`h-full rounded-full ${COLORS[idx % COLORS.length]}`} style={{ width: `${Math.max(pct, 3)}%`, opacity: 0.8 }} />
                  </div>
                  <span className="text-[10px] font-bold text-[#555] w-10 text-right">{count} ({pct.toFixed(0)}%)</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Stalled Deals Table */}
      {staleDeals.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-white overflow-hidden" data-testid="analytics-stalled">
          <div className="px-5 py-3 bg-red-50 border-b border-red-200">
            <h4 className="text-xs font-bold uppercase tracking-wider text-red-700 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> Stalled Deals ({staleDeals.length}) — No activity in 48h+
            </h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#faf7f2] text-[10px] uppercase tracking-wider text-[#888]">
                  <th className="text-left px-4 py-2 font-semibold">Deal</th>
                  <th className="text-center px-4 py-2 font-semibold">Stage</th>
                  <th className="text-center px-4 py-2 font-semibold">Score</th>
                  <th className="text-right px-4 py-2 font-semibold">Last Activity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#f0ebe0]">
                {staleDeals.slice(0, 10).map(d => (
                  <tr key={d.lead_id} className="hover:bg-red-50/50">
                    <td className="px-4 py-2">
                      <div className="font-semibold text-[#0A0A0A]">{d.full_name}</div>
                      <div className="text-[10px] text-emerald-600 font-bold">{d.deal_number}</div>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-200">
                        {STAGE_LABELS[d.stage] || d.stage}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center font-bold text-[#333]">{d.risk_score || "—"}</td>
                    <td className="px-4 py-2 text-right text-[10px] text-[#888]">
                      {d.updated_at ? new Date(d.updated_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══ Admin Deal Card — Design System ═══ */
function AdminDealCard({ deal, index, onView, onNotes, onStageChange, updatingStage }) {
  const loanAmt = deal.requested_loan_amount || deal.purchase_price || 0;
  const ltvPct = deal.ltv ? `${(deal.ltv * 100).toFixed(1)}%` : (deal.arv && loanAmt ? `${((loanAmt / deal.arv) * 100).toFixed(1)}%` : "N/A");
  const arvDisplay = deal.arv ? fmtUSDCompact(deal.arv) : "N/A";
  const loanDisplay = fmtUSDCompact(loanAmt);
  const formatted = formatAddress(deal.property_address || "");
  const parts = formatted.split(",").map(s => s.trim());
  const street = parts[0] || "Address Pending";
  const cityState = parts.length > 1 ? parts.slice(1).join(", ") : (deal.state || "");
  const aiBrief = deal.lender_brief?.headline || deal.lender_brief?.opportunity_summary || "";
  const isFlagged = deal.flagged || deal.extra?.flagged;
  const awaitingApproval = deal.pending_approval || deal.extra?.pending_maker_checker;

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index * 0.04, 0.3) }}>
      <div className="ic-deal-card" style={{ position: "relative" }} data-testid={`admin-deal-card-${deal.lead_id}`}>
        {/* Maker-checker badge */}
        {awaitingApproval && (
          <div style={{
            position: "absolute", top: 10, right: 10, zIndex: 5,
            padding: "3px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700,
            textTransform: "uppercase", letterSpacing: "0.04em",
            background: "rgba(217,119,6,0.1)", color: "#D97706",
          }} data-testid={`awaiting-approval-${deal.lead_id}`}>
            Awaiting Approval
          </div>
        )}

        {/* Map Thumbnail */}
        <div className="ic-deal-card-map" onClick={onView}>
          <MapThumbnail address={deal.property_address} height={140} />
          <span className="ic-deal-card-map-overlay-lt">
            {LOAN_LABELS[deal.loan_type] || deal.loan_type}
          </span>
          {deal.risk_score > 0 && (
            <div className="ic-deal-card-map-overlay-score">
              <ScoreBadge score={deal.risk_score} />
            </div>
          )}
        </div>

        {/* Card Body */}
        <div className="ic-deal-card-body">
          <div className="ic-deal-card-address">{street}</div>
          <div className="ic-deal-card-city">{cityState}</div>
          <div className="ic-deal-card-id">{deal.deal_number || `Deal ${deal.lead_id?.substring(0, 8)}`}</div>

          <div className="ic-deal-card-pills">
            <MetricPill label="Loan" value={loanDisplay} highlight />
            <MetricPill label="LTV" value={ltvPct} />
            <MetricPill label="ARV" value={arvDisplay} />
          </div>

          <div className="ic-deal-card-context">
            {[
              deal.experience > 0 ? `${deal.experience} projects` : null,
              LOAN_LABELS[deal.loan_type] || deal.loan_type,
              deal.interests > 0 ? `${deal.interests} lenders` : null,
              deal.term_sheets > 0 ? `${deal.term_sheets} offers` : null,
            ].filter(Boolean).join(" \u00B7 ")}
          </div>

          {/* Admin info row */}
          <div style={{ fontSize: 12, color: "var(--ic-text-secondary)", marginBottom: 10 }}>
            Submitted by: {deal.email || deal.borrower_email || "Unknown"} &middot; Assigned to: {deal.assigned_to || "Unassigned"}
          </div>

          {aiBrief && (
            <div className="ic-deal-card-ai-brief" title={aiBrief}>{aiBrief}</div>
          )}

          <div className="ic-deal-card-status">
            <StatusBadge status={STAGE_LABELS[deal.stage] || deal.stage} />
            {isFlagged && (
              <span style={{ marginLeft: 8, display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, color: "#DC2626", background: "rgba(220,38,38,0.1)" }}>
                &#9873; Flagged
              </span>
            )}
          </div>

          {/* Action Buttons */}
          <div className="ic-deal-card-buttons">
            <button onClick={onView} className="ic-btn-primary" data-testid={`admin-review-${deal.lead_id}`}>
              Review Deal
            </button>
            {!isFlagged ? (
              <button onClick={onNotes} className="ic-btn-outline" style={{ borderColor: "#D97706", color: "#D97706" }} data-testid={`admin-flag-${deal.lead_id}`}>
                Flag
              </button>
            ) : (
              <span style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 10, borderRadius: "var(--ic-radius)", fontSize: 12, fontWeight: 600, color: "#DC2626", background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.2)" }}>
                &#9873; Flagged
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
